<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import { isPhoneE164OrEmpty, isNumber } from 'shared/helpers/Validators';
import InboxesAPI from 'dashboard/api/inboxes';

import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';

export default {
  components: {
    NextButton,
    NextInput,
  },
  props: {
    enableCallingOnComplete: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      phoneNumber: '',
      apiKey: '',
      phoneNumberId: '',
      businessAccountId: '',
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
  },
  validations: {
    inboxName: { required },
    phoneNumber: { required, isPhoneE164OrEmpty },
    apiKey: { required },
    phoneNumberId: { required, isNumber },
    businessAccountId: { required, isNumber },
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const whatsappChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.inboxName?.trim(),
            channel: {
              type: 'whatsapp',
              phone_number: this.phoneNumber,
              provider: 'whatsapp_cloud',
              provider_config: {
                api_key: this.apiKey,
                phone_number_id: this.phoneNumberId,
                business_account_id: this.businessAccountId,
              },
            },
          }
        );

        if (this.enableCallingOnComplete) {
          try {
            await InboxesAPI.enableWhatsappCalling(whatsappChannel.id);
          } catch (_) {
            useAlert(this.$t('INBOX_MGMT.WHATSAPP_CALLING.ENABLE_FAILED'));
          }
        }

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: whatsappChannel.id,
          },
        });
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <form class="flex flex-col gap-5" @submit.prevent="createChannel()">
    <fieldset class="min-w-0 p-5 border rounded-xl border-n-weak bg-n-solid-1">
      <div class="flex flex-col gap-4">
        <NextInput
          v-model="inboxName"
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL')"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
          :message="
            v$.inboxName.$error
              ? $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR')
              : ''
          "
          :message-type="v$.inboxName.$error ? 'error' : 'info'"
          @blur="v$.inboxName.$touch"
        />
        <NextInput
          v-model="phoneNumber"
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL')"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')"
          :message="
            v$.phoneNumber.$error
              ? $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR')
              : ''
          "
          :message-type="v$.phoneNumber.$error ? 'error' : 'info'"
          @blur="v$.phoneNumber.$touch"
        />
        <NextInput
          v-model="phoneNumberId"
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER_ID.LABEL')"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER_ID.PLACEHOLDER')
          "
          :message="
            v$.phoneNumberId.$error
              ? $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER_ID.ERROR')
              : ''
          "
          :message-type="v$.phoneNumberId.$error ? 'error' : 'info'"
          @blur="v$.phoneNumberId.$touch"
        />
        <NextInput
          v-model="businessAccountId"
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.BUSINESS_ACCOUNT_ID.LABEL')"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.BUSINESS_ACCOUNT_ID.PLACEHOLDER')
          "
          :message="
            v$.businessAccountId.$error
              ? $t('INBOX_MGMT.ADD.WHATSAPP.BUSINESS_ACCOUNT_ID.ERROR')
              : ''
          "
          :message-type="v$.businessAccountId.$error ? 'error' : 'info'"
          @blur="v$.businessAccountId.$touch"
        />
        <NextInput
          v-model="apiKey"
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.LABEL')"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.PLACEHOLDER')"
          :message="
            v$.apiKey.$error ? $t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.ERROR') : ''
          "
          :message-type="v$.apiKey.$error ? 'error' : 'info'"
          @blur="v$.apiKey.$touch"
        />
      </div>
    </fieldset>

    <div class="flex justify-end">
      <NextButton
        :disabled="uiFlags.isCreating"
        :is-loading="uiFlags.isCreating"
        type="submit"
        solid
        blue
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
      />
    </div>
  </form>
</template>
